import React, { useState, useEffect, useCallback } from 'react';
import { CloseXIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';

const VenueGalleryModal = ({
  show,
  images,
  initialIndex = 0,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  useEffect(() => {
    if (show && images.length > 0) {
      const idx = Math.min(Math.max(0, initialIndex), images.length - 1);
      setCurrentIndex(idx);
    }
  }, [show, initialIndex, images.length]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!show) return;

      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [show, goToNext, goToPrevious, onClose]);

  if (!show || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="relative w-full h-full flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white hover:bg-gray-100 transition-colors"
          onClick={onClose}
        >
          <CloseXIcon width="24" height="24" />
        </button>

        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 bg-black bg-opacity-70 rounded-full text-white text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>

        {images.length > 1 && (
          <button
            type="button"
            className="absolute left-4 z-50 p-3 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-lg"
            onClick={goToPrevious}
          >
            <ChevronLeftIcon width="24" height="24" />
          </button>
        )}

        <div className="relative flex min-h-0 min-w-0 w-full max-w-6xl max-h-[90vh] items-center justify-center px-2">
          <img
            src={currentImage.src}
            alt={currentImage.caption || `Photo ${currentIndex + 1}`}
            className="max-h-[85vh] max-w-full w-auto h-auto object-contain object-center self-center"
          />
          {currentImage.caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-4 text-center">
              <p>{currentImage.caption}</p>
            </div>
          )}
        </div>

        {images.length > 1 && (
          <button
            type="button"
            className="absolute right-4 z-50 p-3 rounded-full bg-white hover:bg-gray-100 transition-colors shadow-lg"
            onClick={goToNext}
          >
            <ChevronRightIcon width="24" height="24" />
          </button>
        )}

        <div className="absolute bottom-4 left-1/2 z-50 flex w-[min(100%,42rem)] max-w-[calc(100vw-2rem)] -translate-x-1/2 gap-2 overflow-x-auto px-4 pb-1">
          {images.map((image, index) => (
            <button
              key={index}
              type="button"
              className={`flex-shrink-0 h-16 w-16 rounded overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
              onClick={() => setCurrentIndex(index)}
            >
              <img
                src={image.src}
                alt={`Thumbnail ${index + 1}`}
                className="h-full w-full object-contain object-center bg-neutral-800"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VenueGalleryModal;
